import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Download, FileText, Loader2, Plus, Presentation, RefreshCw, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { ConfirmButton } from '../components/ConfirmButton';
import { proposalApi } from '../services/proposalApi';
import { uploadProjectFile } from '../mocks/workApi';
import { ProposalDeck } from '../components/deck/ProposalDeck';
import { useAlert } from '../contexts/AlertContext';
import { rfpApi } from '../services/rfpApi';
import { FinancePlan, Project } from '../types/project';
import { EvalItem, ScoreTableFile } from '../types/rfp';
import { DraftAllStatus, PROPOSAL_STATUSES, PROPOSAL_STATUS_STYLE, Proposal, ProposalSection, ProposalSummary, SLOT_STATE_STYLE, SOURCE_STYLE, toc } from '../types/proposal';
import { won } from '../utils/format';

/**
 * 제안서 — 공고 → 검토 → 재무계획을 지난 프로젝트가 쓴다. **2026-09-29 부터 실제 DB.**
 *
 *   세 칸 가로 — ① AI 가 볼 자료 · ② 목차(부 · 칸 계층, 칸마다 채움 · 상태 · 평가항목 힌트) · ③ 고른 칸(제목 — 슬라이드 양식이면 한 문장 주장 · 본문 · AI 분석 · 배점 항목 · 출처 · 동작)
 *   좁은 화면에서는 위에서 아래로 쌓인다
 *
 * 「제안서 시작 · AI 초안까지」 는 양식 칸을 깔고 서버의 draft-all 을 부른다 — 룰 칸 먼저, AI 칸은 의존 순서로.
 * 진행은 status 를 3초마다 본다. AI 칸은 승인해야 채움. 실패는 칸 안에 그대로 보인다.
 * 양식은 출발점 — 칸을 더하고(RFP · 기관 목차) · 이름을 바꾸고 · 추가한 칸은 지운다.
 * 더한 칸은 **공통 칸(BASIC 1~6)** 에 대응시켜 그 칸의 지침 · 룰을 받는다(v3). 칸마다 **배점 항목**을 이어 조견표의 「해당 칸」을 만든다.
 */

const NO_EXPORT_PPTX = '칸 전체를 순서대로 한 텍스트(.md)로';
/** 날짜만 — 한국 시간으로(ISO 앞 10자를 자르면 09시 전은 전날이 된다) */
const day = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso.slice(0, 10) : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface Props {
  project: Project;
  plan: FinancePlan | null;
}

/** 룰 이름을 사람 말로 — 내부 키(staffing · eval_matrix …)를 화면에 그대로 보이지 않는다(#113) */
const RULE_LABEL: Record<string, string> = {
  eval_matrix: '배점표', staffing: '투입 인력', references: '수행 실적', price: '제안가 · 재무계획', deliverables: '산출물',
};
const ruleName = (k: string | null | undefined) => (k ? RULE_LABEL[k] ?? '자료' : '');

export const ProposalTab: React.FC<Props> = ({ project, plan }) => {
  const [summary, setSummary] = useState<ProposalSummary | null>(null);
  const [evals, setEvals] = useState<EvalItem[] | null>(null);
  const [rfpReadable, setRfpReadable] = useState<number | null>(null);
  /** 첨부에서 그대로 읽은 배점표 수 — AI 로 뽑지 않는다(2026-10-16) */
  const [scoreTables, setScoreTables] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [instruction, setInstruction] = useState('');
  const [templateCode, setTemplateCode] = useState<string>('');
  const [job, setJob] = useState<DraftAllStatus | null>(null);
  const [adding, setAdding] = useState(false);
  const [newSec, setNewSec] = useState({ code: '', title: '', parentCode: '', basisCode: '', fill: 'AI' as 'AI' | '직접' });
  const [pagePeek, setPagePeek] = useState<{ label: string; text: string } | null>(null);
  const timer = useRef<number | null>(null);

  const proposal = summary?.proposal ?? null;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, evals, files, tables] = await Promise.all([proposalApi.summary(project.id), rfpApi.evalItems(project.id), rfpApi.list(project.id), rfpApi.scoreTables(project.id).catch((): ScoreTableFile[] => [])]);
      setSummary(s);
      setEvals(evals);
      setScoreTables(tables.reduce((n, f) => n + f.tables.length, 0));
      setRfpReadable(files.filter((f) => f.textStatus === '완료' && (f.fileKind === '제안요청서' || f.fileKind === '과업지시서' || f.fileKind === '평가기준')).length);
      setTemplateCode((c) => c || s.suggestedTemplate);
      setJob(await proposalApi.draftAllStatus(project.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : '제안서를 불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  // draft-all 진행 — 도는 동안 3초마다 제안서를 다시 읽는다
  useEffect(() => {
    if (!job?.running) { if (timer.current) window.clearInterval(timer.current); timer.current = null; return; }
    timer.current = window.setInterval(async () => {
      try {
        const st = await proposalApi.draftAllStatus(project.id);
        setJob(st);
        setSummary(await proposalApi.summary(project.id));
        if (!st.running && timer.current) { window.clearInterval(timer.current); timer.current = null; }
      } catch (e) { setError(e instanceof Error ? e.message : '진행 상태를 못 읽었습니다'); }
    }, 3000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [job?.running, project.id]);

  const section = useMemo(() => proposal?.sections.find((s) => s.code === picked) ?? null, [proposal, picked]);
  useEffect(() => { setDraft(section?.content ?? ''); setTitleDraft(section?.title ?? ''); setError(null); }, [section?.code, section?.content, section?.title]);
  useEffect(() => { if (proposal && !picked) setPicked(proposal.sections.find((s) => s.fill !== '절')?.code ?? null); }, [proposal, picked]);

  const run = async (work: () => Promise<Proposal>) => {
    setSaving(true); setError(null);
    try { const p = await work(); setSummary((s) => (s ? { ...s, proposal: p } : s)); }
    catch (e) { setError(e instanceof Error ? e.message : '실패했습니다'); }
    finally { setSaving(false); }
  };

  /** 「제안서 시작 · AI 초안까지」 — 칸을 깔고 draft-all */
  const start = async () => {
    setSaving(true); setError(null);
    try {
      const p = await proposalApi.create(project.id, templateCode || undefined);
      setSummary((s) => (s ? { ...s, proposal: p } : s));
      setJob(await proposalApi.draftAll(project.id, false));
    } catch (e) { setError(e instanceof Error ? e.message : '시작하지 못했습니다'); }
    finally { setSaving(false); }
  };

  const draftAll = async (redo: boolean) => {
    setError(null);
    try { setJob(await proposalApi.draftAll(project.id, redo)); } catch (e) { setError(e instanceof Error ? e.message : '시작하지 못했습니다'); }
  };

  const exportText = async () => {
    setError(null);
    try {
      const { fileName, text } = await proposalApi.exportText(project.id);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e instanceof Error ? e.message : '내려받지 못했습니다'); }
  };

  /* PPT 만들기 — GRK 마스터 레이아웃 24종에 칸을 채운다(#132) */
  const [deckOpen, setDeckOpen] = useState(false);
  const deckButton = (
    <button onClick={() => setDeckOpen(true)} title="GRK 마스터 레이아웃을 골라 칸을 채우고 PPTX 로 — 제안서 칸을 가져올 수 있다" className="px-3 py-1.5 rounded-lg text-xs font-medium border border-indigo-300 text-indigo-700 hover:bg-indigo-50 flex items-center gap-1"><Presentation size={12} /> PPT 만들기</button>
  );

  /* PPTX — 표지 · 목차 · 장 표지 · 칸별 본문. 발주처 양식은 사람이 다듬는다(2026-10-07) */
  const [pptxBusy, setPptxBusy] = useState(false);
  const exportPptx = async () => {
    setError(null);
    setPptxBusy(true);
    try {
      const blob = await proposalApi.exportPptx(project.id);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${project.name.replace(/[\\/:*?"<>|]/g, '_')}_제안서.pptx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } catch (e) { setError(e instanceof Error ? e.message : '내려받지 못했습니다'); } finally { setPptxBusy(false); }
  };

  const peekPage = async (rfpFileId: number, pageNo: number, label: string) => {
    try { const [p] = await rfpApi.pages(project.id, rfpFileId, pageNo, pageNo); setPagePeek({ label, text: p?.text ?? '(없음)' }); }
    catch (e) { setError(e instanceof Error ? e.message : '쪽을 못 읽었습니다'); }
  };

  const planConfirmed = !!plan && plan.status === '확정';
  const evalCount = evals?.length ?? 0;

  if (loading && !summary) {
    return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;
  }

  /* ── 아직 시작 전 ─────────────────────────────────────────── */
  if (!proposal) {
    return (
      <div className="space-y-4">
        {deckOpen && <ProposalDeck projectId={project.id} projectName={project.name} proposal={proposal} onClose={() => setDeckOpen(false)} />}
        <Readiness project={project} planConfirmed={planConfirmed} evalCount={evalCount} rfpReadable={rfpReadable ?? 0} isPublic={templateCode === 'PUBLIC_BID'} />
        {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        <section className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">제안서 시작</h2>
          </div>
          <div className="px-4 py-4 flex flex-wrap items-center gap-3">
            <label className="text-sm text-slate-600" htmlFor="tpl">양식</label>
            <select id="tpl" value={templateCode} onChange={(e) => setTemplateCode(e.target.value)}
              className="min-w-[18rem] text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-indigo-400 focus:outline-none">
              {summary?.templates.map((t) => <option key={t.code} value={t.code}>{t.name} · {t.format} · {t.slots}칸</option>)}
            </select>
            <span className="ml-auto" />
            {deckButton}
            <FinalUpload projectId={project.id} />
            <button onClick={start} disabled={saving}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} 제안서 시작 · AI 초안까지
            </button>
          </div>
        </section>
      </div>
    );
  }

  const parts = toc(proposal.sections);
  const allRequiredFilled = proposal.sections.filter((s) => s.fill !== '절' && s.isRequired).every((s) => s.state === '채움');
  const locked = proposal.status === '제출';
  const slide = proposal.format === '슬라이드';
  const dirty = !!section && (draft !== section.content || titleDraft !== section.title);
  const linkedEvalIds = new Set(proposal.sections.flatMap((s) => s.evalItemIds));
  const unlinkedEvals = (evals ?? []).filter((e) => !linkedEvalIds.has(e.id)).length;

  /* 다른 칸으로 옮기기 전에 쓰던 것을 저장한다 — 전엔 저장 안 한 본문이 조용히 덮였다(#109) */
  const pick = async (code: string) => {
    if (code === picked) return;
    if (section && dirty && !locked && section.fill !== '룰' && section.fill !== '절') {
      try {
        const p = await proposalApi.updateSection(project.id, section.code, { content: draft, ...(titleDraft.trim() && titleDraft !== section.title ? { title: titleDraft.trim() } : {}) });
        setSummary((x) => (x ? { ...x, proposal: p } : x));
      } catch (e) {
        setError(`「${section.title}」을 저장하지 못해 칸을 옮기지 않았습니다 — ${e instanceof Error ? e.message : ''}`);
        return;
      }
    }
    setPicked(code);
  };

  return (
    <div className="space-y-4">
      {deckOpen && <ProposalDeck projectId={project.id} projectName={project.name} proposal={proposal} onClose={() => setDeckOpen(false)} />}
      <Readiness project={project} planConfirmed={planConfirmed} evalCount={evalCount} rfpReadable={rfpReadable ?? 0} isPublic={proposal.templateCode === 'PUBLIC_BID'} />

      {job && (job.running || job.results.length > 0) && (
        <div className={`rounded-lg border px-4 py-2.5 text-xs ${job.running ? 'border-indigo-200 bg-indigo-50 text-indigo-800' : 'border-slate-200 bg-white text-slate-600'}`}>
          <div className="flex items-center gap-2">
            {job.running ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} className="text-emerald-600" />}
            <b>{job.running ? '초안을 쓰는 중' : '초안 끝'}</b>
            <span>{job.done}/{job.total}{job.current ? ` · ${job.current}` : ''}</span>
            {!job.running && <button onClick={() => setJob(null)} className="ml-auto text-indigo-600 hover:underline">닫기</button>}
          </div>
          {job.results.some((r) => !r.ok) && <ul className="mt-1.5 space-y-0.5">{job.results.filter((r) => !r.ok).map((r) => <li key={r.code}>· {r.code} {r.title}: {r.note}</li>)}</ul>}
        </div>
      )}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${PROPOSAL_STATUS_STYLE[proposal.status]}`}>{proposal.status}</span>
        <span className="text-xs text-slate-500">
          {proposal.templateName} · {proposal.format} · v{proposal.version} · {proposal.filled}/{proposal.slots} 채움{proposal.submittedAt && ` · 제출 ${day(proposal.submittedAt)}`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => draftAll(false)} disabled={job?.running || locked} title={locked ? '제출한 제안서는 고치지 않습니다' : '비어 있는 AI 칸 · 룰 칸을 전부 채웁니다'} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"><Sparkles size={12} /> 빈 칸 전부</button>
          {deckButton}
          <FinalUpload projectId={project.id} />
          <button onClick={exportText} title={NO_EXPORT_PPTX} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1"><Download size={12} /> 텍스트로</button>
          <button onClick={exportPptx} disabled={pptxBusy} title="표지 · 목차 · 칸별 슬라이드 — 발주처 양식에 옮겨 붙일 바탕" className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1">{pptxBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PPTX</button>
          <StatusButton proposal={proposal} allFilled={allRequiredFilled} saving={saving} onChange={(s) => run(() => proposalApi.setStatus(project.id, s))} />
          {/* 제출 뒤 다음 할 일 — 전엔 안내가 없었다(#108) */}
          {locked && <span className="text-xs text-emerald-700">제출했습니다 — 결과가 나오면 흐름표의 「수주 판정」에서 판정합니다</span>}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_350px_minmax(0,1fr)]">
        {/* ① 자료 */}
        <Card title="AI 가 볼 자료">
          <ul className="text-xs divide-y divide-slate-100">
            <SourceRow ok={!!project.bid} label="공고 스냅샷">{project.bid ? `${project.bid.source} ${project.bid.bidNtceNo}` : '직접 등록 — 공고 없음'}</SourceRow>
            <SourceRow ok={(rfpReadable ?? 0) > 0} label={`제안요청서 ${rfpReadable ?? 0}건`}>{(rfpReadable ?? 0) > 0 ? '' : '없음 — 공고문으로 씁니다'}</SourceRow>
            <SourceRow ok={evalCount > 0 || scoreTables > 0} label={evalCount > 0 ? `배점표 ${evalCount}항목` : `배점표 ${scoreTables}개`}>{evalCount > 0 && unlinkedEvals > 0 ? `칸에 안 이은 평가요소 ${unlinkedEvals}` : evalCount > 0 || scoreTables > 0 ? '' : '공고 첨부에 없음'}</SourceRow>
            <SourceRow ok={!!project.memo} label="검토 메모">{project.memo || '비어 있음'}</SourceRow>
            <SourceRow ok={planConfirmed} label="재무계획">{plan && planConfirmed ? `확정 · 제안가 ${won(plan.proposalPrice)} · ${plan.ppe ? plan.ppe.manpowerItems.length : plan.manpower.reduce((s, r) => s + r.headcount, 0)}명` : plan ? '임시저장' : '없음'}</SourceRow>
            <SourceRow ok label="아카이브 유사 문서">고객사 · 금액 가림</SourceRow>
          </ul>
        </Card>

        {/* ② 목차 */}
        <Card title="목차" note={proposal.templateName} action={
          !locked && <button onClick={() => setAdding((v) => !v)} title="RFP · 기관이 정한 목차가 양식과 다를 때 칸을 더합니다" className="p-1 rounded text-slate-400 hover:text-indigo-600"><Plus size={14} /></button>
        }>
          {adding && (
            <div className="mb-2 rounded-lg border border-indigo-200 bg-indigo-50 p-2 space-y-1.5 text-xs">
              <div className="flex gap-1.5">
                <input value={newSec.code} onChange={(e) => setNewSec({ ...newSec, code: e.target.value })} placeholder="코드 IV-5" className="w-20 border border-slate-200 rounded px-2 py-1" />
                <input value={newSec.title} onChange={(e) => setNewSec({ ...newSec, title: e.target.value })} placeholder="칸 제목" className="flex-1 border border-slate-200 rounded px-2 py-1" />
              </div>
              <div className="flex gap-1.5">
                <select value={newSec.parentCode} onChange={(e) => setNewSec({ ...newSec, parentCode: e.target.value })} className="flex-1 border border-slate-200 rounded px-1 py-1">
                  <option value="">부 없음</option>
                  {parts.filter((p) => p.part).map((p) => <option key={p.part!.code} value={p.part!.code}>{p.part!.code} {p.part!.title}</option>)}
                </select>
                {!newSec.basisCode && (
                  <select value={newSec.fill} onChange={(e) => setNewSec({ ...newSec, fill: e.target.value as 'AI' | '직접' })} className="border border-slate-200 rounded px-1 py-1"><option value="AI">AI</option><option value="직접">직접</option></select>
                )}
              </div>
              <div className="flex gap-1.5 items-center">
                <select value={newSec.basisCode} onChange={(e) => setNewSec({ ...newSec, basisCode: e.target.value })} title="기관 · RFP 목차 칸이 어느 공통 칸에 해당하나 — 그 칸의 AI 지침 · 룰 · 뼈대를 받습니다" className="flex-1 border border-slate-200 rounded px-1 py-1">
                  <option value="">공통 칸 대응 없음 — 지침 없이</option>
                  {(summary?.basisSlots ?? []).map((b) => <option key={b.code} value={b.code}>공통 {b.code} {b.title} · {b.fill}{b.ruleKey ? ` · ${ruleName(b.ruleKey)}` : ''}</option>)}
                </select>
                <button onClick={() => run(async () => { const p = await proposalApi.addSection(project.id, { code: newSec.code.trim(), title: newSec.title.trim(), parentCode: newSec.parentCode || undefined, ...(newSec.basisCode ? { basisCode: newSec.basisCode } : { fill: newSec.fill }) }); setAdding(false); setNewSec({ code: '', title: '', parentCode: '', basisCode: '', fill: 'AI' }); return p; })} disabled={!newSec.code.trim() || !newSec.title.trim()} className="px-2.5 py-1 rounded-lg font-medium bg-indigo-600 text-white disabled:opacity-40">추가</button>
              </div>
              <p className="text-[11px] text-slate-500">RFP · 기관이 정한 목차를 옮길 때 — 「사업 이해」 · 「수행 방안」 같은 공통 칸에 대응시키면 AI 가 그 지침으로 씁니다.</p>
            </div>
          )}
          <ol className="space-y-2">
            {parts.map((p, i) => (
              <li key={p.part?.code ?? `_${i}`}>
                {p.part && <div className="px-1 pt-1 pb-0.5 text-xs font-semibold text-slate-700">{p.part.code}. {p.part.title}</div>}
                <ul className="space-y-0.5">
                  {p.slots.map((s) => {
                    const on = s.code === picked;
                    return (
                      <li key={s.code}>
                        <button onClick={() => pick(s.code)} className={`w-full text-left rounded-lg border px-2.5 py-1.5 transition ${on ? 'border-indigo-200 bg-indigo-50' : 'border-transparent hover:bg-slate-50'}`}>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[11px] text-slate-400 w-10 flex-shrink-0">{s.code}</span>
                            <span className="text-sm text-slate-900 truncate">{s.title}</span>
                            <span className={`ml-auto rounded-full px-1.5 py-0 text-[10px] font-medium flex-shrink-0 ${SLOT_STATE_STYLE[s.state]}`}>{s.state}</span>
                          </div>
                          <div className="mt-0.5 pl-[46px] flex items-center gap-1 text-[10px] text-slate-400">
                            {s.fill === 'AI' && <Sparkles size={9} />}<span>{s.fill}</span>
                            {s.ruleKey && <span>· {ruleName(s.ruleKey)}</span>}
                            {s.evalCategory && <span>· {s.evalCategory}</span>}
                            {!s.isRequired && <span>· 선택</span>}
                            {s.custom && <span>· 추가한 칸{s.basisCode ? `(공통 ${s.basisCode})` : ''}</span>}
                            {s.evalItemIds.length > 0 && <span>· 배점 {s.evalItemIds.length}</span>}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ol>
        </Card>

        {/* ③ 고른 칸 */}
        {section && (
          <Card
            title={`${section.code} ${section.title}`}
            note={section.fill}
            action={
              <SlotActions
                section={section} saving={saving} aiBusy={!!job?.running} locked={locked} dirty={dirty}
                onFill={() => run(async () => { const r = await proposalApi.fillRule(project.id, section.code); if (r.reason) setError(r.reason); return r.proposal; })}
                onAi={() => run(async () => { const p = await proposalApi.draft(project.id, section.code, instruction || undefined); setInstruction(''); return p; })}
                onSave={() => run(() => proposalApi.updateSection(project.id, section.code, { content: draft, ...(titleDraft.trim() && titleDraft !== section.title ? { title: titleDraft.trim() } : {}) }))}
                onApprove={() => run(() => proposalApi.approve(project.id, section.code))}
                onDiscard={() => run(() => proposalApi.discard(project.id, section.code))}
                onRemove={() => run(() => proposalApi.removeSection(project.id, section.code))}
              />
            }
          >
            {/* 칸 지침 — 더한 칸이면 대응하는 공통 칸의 것 */}
            {section.aiGuide && <p className="mb-3 text-xs text-slate-500 leading-relaxed">{section.basisCode && <span className="mr-1 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-medium text-slate-600">공통 {section.basisCode} 지침</span>}{section.aiGuide}{section.dependsOn.length > 0 && <span className="text-slate-400"> — 먼저 {section.dependsOn.join(' · ')}</span>}</p>}
            {section.custom && !section.basisCode && section.fill === 'AI' && <p className="mb-3 text-xs text-amber-700">공통 칸에 대응시키지 않은 칸이라 AI 는 제목만 보고 씁니다. 지침이 필요하면 지우고 공통 칸을 골라 다시 더합니다.</p>}

            {/* 제목 — 슬라이드 양식이면 한 문장 주장. 사람이 고친다 */}
            {section.fill !== '절' && !locked && (
              <div className="mb-2">
                <label className="text-[11px] font-semibold text-slate-500">{slide ? '슬라이드 제목 — 한 문장 주장' : '칸 제목'}</label>
                <input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} maxLength={100} className="w-full text-sm font-medium border border-slate-200 rounded-lg px-3 py-1.5 focus:border-indigo-400 focus:outline-none" />
              </div>
            )}

            {section.fill === 'AI' && section.state === '비어 있음' ? (
              <div className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center">
                <Sparkles size={18} className="mx-auto text-slate-300" />
                <p className="mt-2 text-sm text-slate-600">AI 가 제안요청서 · 배점표 · 앞 칸 초안을 읽고 {slide ? '슬라이드 원고를' : '이 절을'} 씁니다.</p>
                <p className="mt-1 text-xs text-slate-400">초안은 사람이 승인해야 「채움」이 됩니다. 고객사 이름과 금액은 서버가 가려서 보냅니다.</p>
                <input value={instruction} onChange={(e) => setInstruction(e.target.value)} maxLength={500} placeholder="덧붙일 지시 (선택) — 예: 3단계로, 데이터 중심으로" className="mt-4 w-full max-w-md rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:border-indigo-400 focus:outline-none" />
              </div>
            ) : section.fill === '룰' && section.state === '비어 있음' ? (
              <div className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">비어 있음</div>
            ) : (
              <>
                {section.state === '초안' && (
                  <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1.5">
                    <p>AI 초안입니다 — 고치고 <b>승인</b>해야 채움이 됩니다.{section.aiModel && ` 모델 ${section.aiModel} · ${day(section.aiDraftedAt)}`}</p>
                    {section.aiAnalysis && <details><summary className="cursor-pointer font-medium">AI 가 자료를 어떻게 읽었나</summary><p className="mt-1 whitespace-pre-wrap text-amber-900">{section.aiAnalysis}</p></details>}
                  </div>
                )}
                {section.fill === '직접' && section.state === '비어 있음' && section.content && (
                  <p className="mb-2 text-xs text-slate-500">회사 고정문 · 뼈대가 깔려 있습니다. 고쳐서 저장하면 채움이 됩니다.{section.ruleKey && ' 「자료에서 채우기」로 아카이브 · 재무계획 값을 아래에 붙일 수 있습니다.'}</p>
                )}
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} readOnly={section.fill === '룰' || locked} placeholder={section.fill === '직접' ? '여기에 씁니다' : ''}
                  className={`w-full min-h-[260px] text-sm leading-relaxed border rounded-lg px-3 py-2 resize-y focus:outline-none ${section.fill === '룰' ? 'bg-slate-50 border-slate-100 text-slate-700 font-mono text-xs' : 'border-slate-200 focus:border-indigo-400'}`} />
                {section.fill === '룰' && <p className="mt-1 text-xs text-slate-400">룰 칸은 자료를 옮긴 것이라 여기서 고치지 않습니다. 원본을 고치고 다시 채웁니다.</p>}
                {section.fill === 'AI' && section.state === '초안' && (
                  <input value={instruction} onChange={(e) => setInstruction(e.target.value)} maxLength={500} placeholder="다시 쓸 때 덧붙일 지시 (선택)" className="mt-2 w-full max-w-md rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:border-indigo-400 focus:outline-none" />
                )}
              </>
            )}

            {/* 배점 항목 — 이 칸이 다루는 평가요소. 공공 첫 장 조견표의 「해당 칸」 */}
            {evals && evals.length > 0 && section.fill !== '절' && (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="text-xs font-semibold text-slate-500">배점 항목 <span className="font-normal text-slate-400">— 이 칸이 다루는 평가요소. 조견표의 「해당 칸」이 됩니다{section.evalCategory ? ` · 힌트 「${section.evalCategory}」` : ''}</span></div>
                <ul className="mt-1 grid gap-x-3 gap-y-0.5 sm:grid-cols-2">
                  {evals.map((e) => {
                    const on = section.evalItemIds.includes(e.id);
                    const toggle = () => run(() => proposalApi.setSectionEvals(project.id, section.code, on ? section.evalItemIds.filter((x) => x !== e.id) : [...section.evalItemIds, e.id]));
                    return (
                      <li key={e.id}>
                        <label className={`flex items-start gap-1.5 text-xs cursor-pointer rounded px-1 py-0.5 ${on ? 'bg-violet-50 text-violet-900' : 'text-slate-600 hover:bg-slate-50'}`}>
                          <input type="checkbox" checked={on} disabled={saving || locked} onChange={toggle} className="mt-0.5" />
                          <span className="min-w-0"><span className="text-slate-400">{e.category}</span> · {e.item}{e.points != null && <span className="text-slate-400"> · {e.points}점</span>}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="text-xs font-semibold text-slate-500">출처</div>
              {section.sources.length === 0 ? (
                <p className="mt-1 text-xs text-slate-400">아직 없습니다.</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {section.sources.map((src) => (
                    <li key={src.id} className="flex items-center gap-2 text-xs">
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium flex-shrink-0 ${SOURCE_STYLE[src.kind]}`}>{src.kind}</span>
                      <span className="text-slate-700 truncate">{src.label}</span>
                      {src.kind === '제안요청서' && src.rfpFileId && src.pageNo && (
                        <button onClick={() => peekPage(src.rfpFileId!, src.pageNo!, src.label)} title="그 쪽의 텍스트를 봅니다" className="ml-auto text-indigo-600 hover:underline flex-shrink-0"><FileText size={12} className="inline" /> p.{src.pageNo}</button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {pagePeek && (
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="flex items-center justify-between text-xs text-slate-500"><span>{pagePeek.label}</span><button onClick={() => setPagePeek(null)} className="text-indigo-600 hover:underline">닫기</button></div>
                  <pre className="mt-1 whitespace-pre-wrap text-xs text-slate-700 max-h-48 overflow-y-auto">{pagePeek.text}</pre>
                </div>
              )}
              {section.editedAt && <p className="mt-2 text-[11px] text-slate-400">마지막 수정 {day(section.editedAt)}</p>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

/* ── 부품 ─────────────────────────────────────────────────── */

const Readiness: React.FC<{ project: Project; planConfirmed: boolean; evalCount: number; rfpReadable: number; isPublic: boolean }> = ({ project, planConfirmed, evalCount, rfpReadable, isPublic }) => {
  // 짧게 — 무엇이 빠졌는지만(2026-10-16). 배점표는 AI 로 뽑지 않아 경고하지 않는다
  void evalCount; void isPublic;
  const warnings = [
    project.bid && rfpReadable === 0 && '읽을 수 있는 제안요청서 · 과업지시서가 없습니다',
    !planConfirmed && '재무계획이 확정되지 않았습니다',
  ].filter(Boolean) as string[];
  if (warnings.length === 0) return null;
  return <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 space-y-0.5">{warnings.map((w) => <p key={w}>· {w}</p>)}</div>;
};

const Card: React.FC<{ title: string; note?: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, note, action, children }) => (
  <section className="bg-white rounded-lg border border-slate-200 self-start min-w-0">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
      <h2 className="text-sm font-semibold text-slate-900 truncate">{title}</h2>
      {note && <span className="text-xs text-slate-400 flex-shrink-0">{note}</span>}
      {action && <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">{action}</div>}
    </div>
    <div className="px-4 py-3">{children}</div>
  </section>
);

const SourceRow: React.FC<{ ok: boolean; label: string; children: React.ReactNode }> = ({ ok, label, children }) => (
  <li className="py-1.5 flex gap-2">
    <span className={`mt-0.5 w-3.5 h-3.5 rounded-full grid place-items-center flex-shrink-0 ${ok ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-300'}`}>{ok ? <Check size={9} strokeWidth={3} /> : null}</span>
    <span className="min-w-0"><span className="font-medium text-slate-700">{label}</span><span className="block text-slate-400 truncate">{children}</span></span>
  </li>
);

const btn = 'px-2.5 py-1 rounded-lg text-xs font-medium border flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed';
const btnPlain = `${btn} border-slate-300 text-slate-700 hover:bg-slate-50`;
const btnMain = `${btn} border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700`;

const SlotActions: React.FC<{
  section: ProposalSection; saving: boolean; aiBusy: boolean; locked: boolean; dirty: boolean;
  onFill: () => void; onAi: () => void; onSave: () => void; onApprove: () => void; onDiscard: () => void; onRemove: () => void;
}> = ({ section, saving, aiBusy, locked, dirty, onFill, onAi, onSave, onApprove, onDiscard, onRemove }) => {
  // 되돌릴 수 없다 — 한 번 더 누른다(#108). 칸 지우기는 「비우기」와 아이콘을 달리한다
  const remove = section.custom && !locked && <ConfirmButton onConfirm={onRemove} disabled={saving} title="추가한 칸을 지웁니다" ask="한 번 더 — 칸을 지웁니다" className={`${btn} border-transparent text-slate-400 hover:text-red-600 hover:bg-red-50`}><X size={12} /> 칸</ConfirmButton>;
  const fill = section.ruleKey && !locked && (
    <button onClick={onFill} disabled={saving} title={`${ruleName(section.ruleKey)} 자료에서 옮깁니다`} className={btnPlain}><RefreshCw size={12} /> {section.state === '채움' ? '다시 채우기' : '자료에서 채우기'}</button>
  );
  if (section.fill === '룰') return <>{fill}{remove}</>;
  if (section.fill === 'AI') {
    return (
      <>
        <button onClick={onAi} disabled={saving || aiBusy || locked || section.state === '채움'}
          title={locked ? '제출한 제안서는 고치지 않습니다' : section.state === '채움' ? '승인된 칸입니다. 다시 쓰려면 먼저 비웁니다' : section.state === '초안' ? '지금 초안을 바탕으로 다시 씁니다' : '제안요청서 · 배점표를 근거로 초안을 받습니다'}
          className={section.state === '비어 있음' ? btnMain : btnPlain}>
          {saving || aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {section.state === '초안' ? '다시 쓰기' : 'AI 초안'}
        </button>
        {section.state !== '비어 있음' && !locked && (
          <>
            <button onClick={onSave} disabled={saving || !dirty} className={btnPlain}>저장</button>
            {section.state === '초안' && <button onClick={onApprove} disabled={saving || dirty} title={dirty ? '먼저 저장합니다' : undefined} className={btnMain}><Check size={12} /> 승인</button>}
            <ConfirmButton onConfirm={onDiscard} disabled={saving} title="본문을 비웁니다" ask="한 번 더 — 본문을 비웁니다" className={`${btn} border-transparent text-slate-400 hover:text-red-600 hover:bg-red-50`}><Trash2 size={12} /></ConfirmButton>
          </>
        )}
        {remove}
      </>
    );
  }
  return (
    <>
      {fill}
      <button onClick={onSave} disabled={saving || !dirty || locked} className={btnMain}>저장</button>
      {section.state === '채움' && !locked && <ConfirmButton onConfirm={onDiscard} disabled={saving} title="뼈대로 되돌립니다" ask="한 번 더 — 뼈대로 되돌립니다" className={`${btn} border-transparent text-slate-400 hover:text-red-600 hover:bg-red-50`}><Trash2 size={12} /></ConfirmButton>}
      {remove}
    </>
  );
};

/** 상태를 다음으로 넘기는 버튼 하나. 되돌리기는 없다 — 판이 올라가는 것으로 본다 */
const StatusButton: React.FC<{ proposal: Proposal; allFilled: boolean; saving: boolean; onChange: (s: Proposal['status']) => void }> = ({ proposal, allFilled, saving, onChange }) => {
  const i = PROPOSAL_STATUSES.indexOf(proposal.status);
  const next = PROPOSAL_STATUSES[i + 1];
  if (!next) return null;
  const blocked = next === '검토 중' && !allFilled;
  const label = next === '검토 중' ? '검토 요청' : next === '승인' ? '승인' : '제출 완료로 표시';
  const why = blocked ? '필수 칸을 전부 채워야 검토를 요청할 수 있습니다' : next === '제출' ? '실제 제출은 발주처 시스템에서 합니다. 여기에는 제출한 사실만 적습니다' : undefined;
  // 상태는 되돌리지 않는다 — 특히 제출은 제안서를 잠근다. 한 번 더 누른다(#108)
  return (
    <ConfirmButton onConfirm={() => onChange(next)} disabled={saving || blocked} title={why}
      ask={next === '제출' ? '한 번 더 — 제출로 잠급니다' : `한 번 더 — ${label}`}
      armedClassName="px-4 py-1.5 rounded-lg text-xs font-medium bg-indigo-800 text-white whitespace-nowrap"
      className="px-4 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">{label}</ConfirmButton>
  );
};

/**
 * 최종 제안서 올리기 — 나라장터에 낸 최종본(PPT · HWP · PDF)을 드라이브 사업 폴더 「02.제안서」에 저장하고
 * 아카이브에 바로 등록한다(2026-10-16). 실주해도 남는다 — 다음 제안의 재료. 앱 밖에서 쓴 제안서도 여기서 올린다
 */
const FinalUpload: React.FC<{ projectId: number }> = ({ projectId }) => {
  const { showToast } = useAlert();
  const [busy, setBusy] = useState(false);
  return (
    <label className={`px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 flex items-center gap-1 ${busy ? 'opacity-40' : 'cursor-pointer hover:bg-slate-50'}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} 최종본 올리기
      <input type="file" className="hidden" disabled={busy} onChange={async (e) => {
        const f = e.target.files?.[0]; e.target.value = '';
        if (!f) return;
        setBusy(true);
        try { const d = await uploadProjectFile(projectId, '제안서', f); showToast(`드라이브 「02.제안서」에 올리고 아카이브에 등록했습니다 — ${d.fileName}`, 'success'); }
        catch (err) { showToast(err instanceof Error ? err.message : '올리지 못했습니다', 'error'); }
        finally { setBusy(false); }
      }} />
    </label>
  );
};
